import { Injectable } from '@angular/core';
import _ from 'lodash';
import { Rect } from '../models/rect';
import { PageService } from './page.service';
import { CrudService } from './crud.service';
import { ApplicationStateService } from './application-state.service';
import { CommunicationService } from './communication.service';
import { BookFeatureDataChangedEvent } from '../utils/events/book-feature-data-changed-event';
import { RectFactory } from '../models/factories/rect-factory';
import { RouteService } from './route.service';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class RectService {

    /**
     * dummyId is used to assign an id for rects which do not have a real id yet.
     * Start with -2 so, functions which return -1 will not be problematic. Value should be drecremented.
     * @type {number}
     */
    private static dummyId = -2;
    private rects: Array<Rect>;
    private indexToRectMap: Map<number, Array<Rect>> = new Map();
    private dummyIdIndexMap: Map<number, number> = new Map();

    private pendingRects = new Map<number, string>();

    constructor(private applicationStateService: ApplicationStateService,
                private communicationService: CommunicationService,
                private pageService: PageService,
                private routeService: RouteService,
                private crudService: CrudService) {
    }

    setRects(rects: Array<Rect>): void {
        this.rects = [];
        this.indexToRectMap = new Map();
        rects.forEach(entity => this.includeToIndexMap(entity));
    }

    /**
     * Similar to setRects(rects) but in addition the passed values are transformed in forehand.
     * This method purpose is to do the transformation and the storing in just one for-Loop.
     * @param objects
     */
    setObjectsToRects(objects: Array<any>): void {
        this.rects = [];
        this.indexToRectMap = new Map();
        const rectFactory = new RectFactory();
        objects.forEach(entity => {
            this.includeToIndexMap(rectFactory.buildFromObject(entity));
        });
    }

    getRects(): Promise<Array<Rect>> {

        return new Promise<Array<Rect>>(resolve => {
            const thiz = this;

            if (!thiz.applicationStateService.bookConfig.bookPermissions.rect.view) {
                thiz.rects = [];
            }

            if (thiz.rects) {
                resolve(thiz.rects);

                return;
            }

            return thiz.crudService.getAllEntitiesOfBook(Rect, new RectFactory())
                .then(rects => {
                    thiz.setRects(rects as Array<Rect>);
                    resolve(rects as Array<Rect>);
                });
        });
    }

    getRectsOfPage(index: number): Promise<Array<Rect>> {

        return new Promise(resolve => {
            this.getRects()
                .then(() => {
                    if (this.indexToRectMap.get(index)) {
                        resolve(this.indexToRectMap.get(index));
                    } else {
                        resolve([]);
                    }
                });
        });
    }

    getRectsOfPages(pageIndex: number): Promise<Array<Rect>> {

        return new Promise<Array<Rect>>(resolve => {
            let rects = [];
            const promises = [];
            // Add the the promises of all search pages to the array
            for (let runningPageIndex = 0;
                 runningPageIndex < this.applicationStateService.currentViewMode.pagesToShow;
                 runningPageIndex++) {
                promises.push(this.getRectsOfPage(pageIndex + runningPageIndex));
            }

            Promise.all(promises)
                .then(values => {
                    values.forEach(value => rects = rects.concat(value));
                    resolve(rects as Array<Rect>);
                });
        });
    }

    createRect(rect: Rect): Promise<Rect> {

        return new Promise<Rect>(resolve => {
            const id = rect.id;
            const indexOfRect = this.dummyIdIndexMap.get(rect.id);
            const stringifiedRect = JSON.stringify(rect);

            if (this.pendingRects.get(id)) {
                this.pendingRects.set(id, stringifiedRect);

                return;
            }

            this.pendingRects.set(id, stringifiedRect);
            this.crudService.create(rect)
                .then(result => {

                    const pendingRectAsString = this.pendingRects.get(id);
                    const rectHasChanged = stringifiedRect !== pendingRectAsString;

                    this.removeRectFromMap(rect);
                    const rectFactory = new RectFactory();
                    rect = rectFactory.buildFromObject(result);
                    this.addRectToMap(rect);
                    this.rects[indexOfRect] = rect;
                    const event = BookFeatureDataChangedEvent.createEvent(rect);
                    event.cacheOnly = true;
                    this.communicationService.announceBookFeatureDataChanged(event);

                    if (rectHasChanged) {
                        const updatedRect = JSON.parse(pendingRectAsString);
                        updatedRect.id = result.id;
                        this.updateRect(updatedRect);
                    }

                    this.logUsage(rect);
                    resolve(result as Rect);
                });
        });
    }

    storeRectLocally(rect: Rect): Rect {

        this.assignDummyId(rect);
        this.rects.push(rect);
        const indexOfRect = this.rects.length - 1;
        this.dummyIdIndexMap.set(rect.id, indexOfRect);
        this.addRectToMap(rect);
        rect.updatedAt = { date: new Date() };
        const event = BookFeatureDataChangedEvent.createEvent(rect);
        event.cacheOnly = true;
        this.communicationService.announceBookFeatureDataChanged(event);

        return rect;
    }

    updateRect(rect: Rect): Promise<Rect> {
        return new Promise<Rect>(resolve => {
            const indexToUpdate = this.getIndexOfRect(rect);
            this.rects[indexToUpdate] = rect;

            this.crudService.update(rect)
                .then(result => {
                    this.communicationService.announceBookFeatureDataChanged(BookFeatureDataChangedEvent.createEvent(rect));
                    this.logUsage(rect);

                    return new Promise(resolve => resolve(result as Rect));
                });
        });
    }

    deleteRect(rect: Rect): Promise<boolean> {

        return new Promise<boolean>(resolve => {
            this.getIndexOfRect(rect);
            if (!rect || !rect.id) {
                resolve(false);

                return;
            }

            const indexToDelete = this.getIndexOfRect(rect);
            this.rects.splice(indexToDelete, 1);
            this.removeRectFromMap(rect);
            this.communicationService.announceBookFeatureDataChanged(BookFeatureDataChangedEvent.createEvent(rect));

            this.crudService.remove(rect)
                .then(result => {
                    this.logUsage(rect);
                    resolve(result as boolean);
                });
        });
    }

    logUsage(rect: Rect): Promise<boolean> {
        return this.crudService.log(rect) as Promise<boolean>;
    }

    getExportNotesAsRTFLink(): string {
        const route = this.routeService.getAbsoluteRoute(environment.endpointUrls.noteExport, {
            bookId: this.applicationStateService.bookId
        });

        return route;
    }

    isElementInPointRect($event): boolean {

        const path = $event.srcEvent.path;
        let contains = false;

        if (!path) {
            return false;
        }
        path.some(ele => {
            if (ele.className && ele.className.includes('point-rect')) {
                contains = true;
            }
        });

        return contains;
    }

    private addRectToMap(rect: Rect): void {

        const indexOfRect = this.pageService.getIndexByPageId(rect.pageId);
        rect.index = indexOfRect;

        if (!this.indexToRectMap.get(indexOfRect)) {
            this.indexToRectMap.set(indexOfRect, [rect]);
        } else {
            this.indexToRectMap.get(indexOfRect)
                .push(rect);
        }
    }

    private removeRectFromMap(rect: Rect): void {

        const indexOfRect = this.pageService.getIndexByPageId(rect.pageId);

        const rectsOfPage = this.indexToRectMap.get(indexOfRect);
        const indexOfRectInPage = _.findIndex(rectsOfPage, { id: rect.id });
        rectsOfPage.splice(indexOfRectInPage, 1);
    }

    private getIndexOfRect(searchedRect: Rect): number {
        const index = _.findIndex(this.rects, { id: searchedRect.id });

        return index;
    }

    private assignDummyId(rect: Rect): void {
        --RectService.dummyId;
        rect.id = RectService.dummyId;
    }

    private includeToIndexMap(rect: Rect): void {
        this.rects.push(rect);
        this.addRectToMap(rect);
    }
}
